import { doc, getDoc } from 'firebase/firestore';
import { format } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { isEmpty } from 'lodash';
import IOrder from '~/interfaces/IOrder';

import Dashboard from '~/layouts/Dashboard';
import { DB } from '~/services/firebase';
import getCurrencyFormatted from '~/utils/getCurrencyFormatted';
import { useAuth } from '~/hooks/auth';
import {
  ORDER_STATUS,
  PAYMENT_METHODS,
  PRINTER_MODELS,
} from '~/utils/getEnums';
import getAddressFormatted from '~/utils/getAddressFormatted';
import { useOrders } from '~/hooks/orders';
import { useToast } from '~/hooks/toast';

import { handlePrint } from '~/utils/handlePrint';

interface Params {
  id: string;
}

const OrderDetails: React.FC = () => {
  const params = useParams<Params>();
  const { id } = params;
  const { company } = useAuth();
  const { updateOrder } = useOrders();
  const { addToast } = useToast();

  const { goBack } = useHistory();

  const [order, setOrder] = useState({} as IOrder);
  const [loading, setLoading] = useState(false);

  const parseProductsChatbot = useMemo(() => {
    let products = '';
    if (order.chatbot) {
      products = (order.products as any).replace('\n', '');
      products = products.replace('\n', '<br />');
    }

    return products;
  }, [order]);

  useEffect(() => {
    async function loadOrder() {
      const ordersRef = doc(DB, 'orders', id);
      const docSnap = await getDoc(ordersRef);
      const data = docSnap.data() as IOrder;
      const created_at = new Date(data.created_at.seconds * 1000);

      setOrder({
        ...data,
        uid: id,
        additionValue_formatted:
          data.additionValue && data.additionValue > 0
            ? getCurrencyFormatted(data.additionValue)
            : '',
        shippingCost_formatted: getCurrencyFormatted(data.shippingCost),
        subtotal_formatted: getCurrencyFormatted(data.subtotal),
        total_formatted: getCurrencyFormatted(data.total),
        created_at_formatted: format(created_at, 'dd/MM/yyyy HH:mm'),
      } as IOrder);
    }

    if (id) loadOrder();
  }, [id]);

  const handleSubmit = useCallback(async () => {
    setLoading(true);

    await updateOrder(id, order);

    addToast({
      type: 'success',
      description: 'Os valores do pedido foram atualizados com sucesso!',
    });

    setLoading(false);
  }, [order, updateOrder, addToast, id]);

  const couponDiscount = useMemo(() => {
    if (!order.coupon || !order.coupon.uid) return 0;

    return order.coupon.type === 0
      ? parseFloat(((order.subtotal * order.coupon.value) / 100).toFixed(2))
      : order.coupon.value;
  }, [order]);

  return (
    <Dashboard>
      <div className="nk-content-inner">
        <div className="nk-content-body">
          <div className="nk-block-head">
            <div className="nk-block-between g-3">
              <div className="nk-block-head-content">
                <h3 className="nk-block-title page-title">
                  Pedido{' '}
                  {order.uid && (
                    <strong className="text-primary small">{`#${order.code}`}</strong>
                  )}
                </h3>
                <div className="nk-block-des text-soft">
                  <ul className="list-inline">
                    <li>
                      Data e Hora:{' '}
                      <span className="text-base">
                        {order.created_at_formatted}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="nk-block-head-content">
                <Link
                  to="/orders"
                  className="btn btn-outline-light bg-white d-inline-flex"
                >
                  <em className="icon ni ni-arrow-left" />
                  <span>Voltar</span>
                </Link>
              </div>
            </div>
          </div>
          <div className="nk-block">
            <div className="invoice">
              <div className="invoice-action">
                <button
                  type="button"
                  className="btn btn-icon btn-lg btn-white btn-dim btn-outline-primary"
                  onClick={() => handlePrint({ order, company })}
                >
                  <em className="icon ni ni-printer-fill" />
                </button>
              </div>
              <div className="invoice-wrap">
                <div>
                  <img
                    src={company.image}
                    alt="logo"
                    style={{ height: 150, objectFit: 'contain' }}
                  />
                </div>
                <div className="invoice-head">
                  <div className="invoice-contact">
                    <span className="overline-title">Cliente</span>
                    <div className="invoice-contact-info">
                      <h4 className="title">
                        {order.name}
                        {order.client_order_count === 1 && (
                          <span
                            className="badge bg-success ml-2"
                            style={{ fontSize: 12, verticalAlign: 'middle' }}
                          >
                            🎉 PRIMEIRO PEDIDO
                          </span>
                        )}
                      </h4>
                      <ul className="list-plain">
                        <li>
                          <em className="icon ni ni-map-pin-fill" />
                          {order.chatbot && <span>{order.address}</span>}
                          {!order.chatbot && (
                            <span>
                              {isEmpty(order.address) ||
                              (order.address && order.address.road)
                                ? getAddressFormatted(order.address)
                                : order.address}
                              {/* <br /> */}
                            </span>
                          )}

                          {/* {order.address && order.address.complement && (
                            <span>{`, ${order.address.complement}`}</span>
                          )}
                          {order.address && order.address.reference && (
                            <span>{`, ${order.address.reference}`}</span>
                          )} */}
                        </li>
                        <li>
                          <em className="icon ni ni-call-fill" />
                          <span>{order.whatsapp}</span>
                        </li>
                        {order.chatbot && (
                          <li>
                            <em className="icon fa fa-credit-card" />
                            <span style={{ lineHeight: 1 }}>
                              {PAYMENT_METHODS[order.paymentMethod]}
                            </span>
                          </li>
                        )}
                        {order.pix_file && (
                          <li>
                            <a
                              href={order.pix_file}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                textTransform: 'uppercase',
                                fontWeight: 'bold',
                              }}
                            >
                              Visualizar Comprovante Pix
                            </a>
                          </li>
                        )}
                        {order.chatbot && (
                          <li>
                            <p>
                              <em className="icon bi bi-receipt" />
                              <strong>Pedido realizado pelo cliente:</strong>
                            </p>
                            <div
                              dangerouslySetInnerHTML={{
                                __html: parseProductsChatbot,
                              }}
                            />
                            {/* <span style={{ whiteSpace: 'break-spaces' }}>
                              {parseProductsChatbot}
                            </span> */}
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>

                  <div className="invoice-desc">
                    <h3 className="title">Pedido</h3>
                    {order.uid && (
                      <ul className="list-plain">
                        <li className="invoice-id">
                          <span>{`Código: #${order.code}`}</span>
                        </li>
                        <li className="invoice-date">
                          <span>{`Data: ${order.created_at_formatted}`}</span>
                        </li>
                      </ul>
                    )}
                  </div>
                </div>

                {order.chatbot && (
                  <div className="row align-items-end">
                    <div className="col-md-2 form-group mb-3">
                      <label className="form-label">Total do Pedido (R$)</label>
                      <div className="form-control-wrap">
                        <input
                          type="number"
                          className="form-control"
                          defaultValue={order.subtotal}
                          onChange={ev =>
                            setOrder({
                              ...order,
                              subtotal: parseFloat(ev.target.value),
                            })
                          }
                        />
                        {/* {errors.postcode && (
                                  <span className="invalid">
                                    {errors.postcode}
                                  </span>
                                )} */}
                      </div>
                    </div>

                    <div className="col-md-2 form-group mb-3">
                      <label className="form-label">Taxa de entrega (R$)</label>
                      <div className="form-control-wrap">
                        <input
                          type="number"
                          className="form-control"
                          defaultValue={order.shippingCost}
                          onChange={ev =>
                            setOrder({
                              ...order,
                              shippingCost: parseFloat(ev.target.value),
                            })
                          }
                        />
                        {/* {errors.postcode && (
                                  <span className="invalid">
                                    {errors.postcode}
                                  </span>
                                )} */}
                      </div>
                    </div>

                    <div className="form-group">
                      <button
                        type="submit"
                        className="btn btn-lg btn-primary"
                        disabled={loading}
                        onClick={handleSubmit}
                      >
                        {loading && (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          />
                        )}
                        {!loading && 'Salvar'}
                      </button>
                    </div>
                  </div>
                )}
                <div className="invoice-bills">
                  {!order.chatbot && (
                    <div className="table-responsive">
                      <table className="table">
                        <thead>
                          <tr>
                            <th className="w-150px">#</th>
                            <th className="w-60">Item</th>
                            <th>Valor</th>
                            <th>Qtd</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {order.products &&
                            order.products.map((product, index) => (
                              <>
                                <tr>
                                  <td>{`#${index + 1}`}</td>
                                  <td>
                                    {`${product.name}`}

                                    {product.observations && (
                                      <span style={{ marginLeft: 8 }}>
                                        {`(Obs: ${product.observations})`}
                                      </span>
                                    )}
                                  </td>
                                  <td>
                                    {getCurrencyFormatted(
                                      parseFloat(product.value),
                                    )}
                                  </td>
                                  <td>
                                    {product.measure === 'KG'
                                      ? product.quantity.toFixed(3)
                                      : product.quantity}
                                    {product.measure
                                      ? product.measure.toLocaleLowerCase()
                                      : ''}
                                  </td>
                                  <td>
                                    {getCurrencyFormatted(
                                      parseFloat(product.value) *
                                        product.quantity,
                                    )}
                                  </td>
                                </tr>

                                {product.complements &&
                                  product.complements.length > 0 && (
                                    <tr>
                                      <td
                                        colSpan={5}
                                        style={{
                                          fontSize: 10,
                                          textAlign: 'left',
                                        }}
                                      >
                                        {product.complements.map(complement => (
                                          <span style={{ marginRight: 8 }}>
                                            {`${complement.quantity}x - ${complement.item.name}`}
                                          </span>
                                        ))}
                                      </td>
                                    </tr>
                                  )}
                              </>
                            ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan={2} />
                            <td colSpan={2}>Forma de pagamento</td>

                            <td>
                              {order.uid &&
                              order.orderPayments &&
                              order.orderPayments.length > 0
                                ? order.orderPayments.map((item, index) => (
                                    <p
                                      className="mb-0"
                                      key={`${item.payment_method_id}-${index}`}
                                    >
                                      {PAYMENT_METHODS[item.payment_method_id]}{' '}
                                      - {item.value_formatted}
                                    </p>
                                  ))
                                : PAYMENT_METHODS[order.paymentMethod]}
                            </td>
                          </tr>
                          {order.additionValue_formatted && (
                            <tr>
                              <td colSpan={2} />
                              <td colSpan={2}>Acréscimo</td>
                              <td>{order.additionValue_formatted}</td>
                            </tr>
                          )}
                          {order.coupon && order.coupon.uid && (
                            <tr>
                              <td colSpan={2} />
                              <td colSpan={2}>Cupom</td>
                              <td>{order.coupon.code}</td>
                            </tr>
                          )}
                          {order.paymentMethod === '0' && (
                            <tr>
                              <td colSpan={2} />
                              <td colSpan={2}>Troco para</td>
                              <td>{order.thing || 'R$ 0,00'}</td>
                            </tr>
                          )}
                          <tr>
                            <td colSpan={2} />
                            <td colSpan={2}>Subtotal</td>
                            <td>{getCurrencyFormatted(order.subtotal)}</td>
                          </tr>
                          {order.shippingCost > 0 && (
                            <tr>
                              <td colSpan={2} />
                              <td colSpan={2}>Taxa de entrega</td>
                              <td>
                                {getCurrencyFormatted(order.shippingCost)}
                              </td>
                            </tr>
                          )}
                          {couponDiscount > 0 && (
                            <tr>
                              <td colSpan={2} />
                              <td colSpan={2}>Desconto do cupom</td>
                              <td>{getCurrencyFormatted(couponDiscount)}</td>
                            </tr>
                          )}
                          {order.fidelitDiscount &&
                          order.fidelitDiscount > 0 ? (
                            <tr>
                              <td colSpan={2} />
                              <td colSpan={2}>Desconto Fidelidade</td>
                              <td>
                                {getCurrencyFormatted(order.fidelitDiscount)}
                              </td>
                            </tr>
                          ) : (
                            <></>
                          )}
                          <tr>
                            <td colSpan={2} />
                            <td colSpan={2}>Total</td>
                            <td>{getCurrencyFormatted(order.total)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dashboard>
  );
};

export default OrderDetails;
